import React from 'react';
import { Pressable, Text, View } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';

export type LocationSuggestion = {
  title: string;
  subtitle?: string;
  icon?: string;
  value?: string; // full label (e.g., Google description)
  isNearby?: boolean; // special flag for "search nearby" option
};

interface Props {
  visible: boolean;
  suggestions: LocationSuggestion[];
  onSelect: (s: LocationSuggestion) => void;
  showNearbyOption?: boolean; // show "Search nearby" at top
  style?: any; // container style override (absolute positioning handled by parent)
}

const LocationSuggestions: React.FC<Props> = ({ visible, suggestions, onSelect, showNearbyOption = true, style }) => {
  if (!visible) return null;

  const nearbyOption: LocationSuggestion = {
    title: 'Search nearby',
    subtitle: 'Find camps within 20 miles of your location',
    icon: 'crosshairs-gps',
    isNearby: true,
  };

  const allSuggestions = showNearbyOption ? [nearbyOption, ...suggestions] : suggestions;

  return (
    <View
      style={{
        position: 'absolute',
        top: 56,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderRadius: 16,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: 0.15,
        shadowRadius: 16,
        elevation: 8,
        paddingVertical: 8,
        maxHeight: 360,
        overflow: 'hidden',
        zIndex: 9999,
        ...style,
      }}
    >
      <Text
        style={{
          fontSize: 12,
          color: '#666',
          paddingHorizontal: 16,
          paddingVertical: 8,
          fontWeight: '500',
        }}
      >
        Suggested destinations
      </Text>

      {allSuggestions.map((s, idx) => (
        <Pressable
          key={`${s.title}-${idx}`}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderTopWidth: idx === 0 ? 0 : 1,
            borderTopColor: '#F2F2F2',
            backgroundColor: s.isNearby ? '#F8FFF9' : 'transparent',
          }}
          onPress={() => onSelect(s)}
        >
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: s.isNearby ? '#E8F5E9' : '#F3F6FB',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 12,
            }}
          >
            <MaterialCommunityIcons
              // casting to any to avoid overly strict glyph typing from the icon package
              name={(s.icon || 'map-marker') as any}
              size={18}
              color={s.isNearby ? '#2E7D32' : '#3C8248'}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{
              fontSize: 14,
              color: s.isNearby ? '#2E7D32' : '#222',
              fontWeight: s.isNearby ? '700' : '600'
            }}>
              {s.title}
            </Text>
            {s.subtitle ? (
              <Text style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{s.subtitle}</Text>
            ) : null}
          </View>
        </Pressable>
      ))}
    </View>
  );
};

export default LocationSuggestions; 